import {
  ArrowLeftIcon,
  Button,
  Caption,
  css,
  EnterIcon,
  Inline,
  Paragraph,
  Spinner,
  Stack,
  styled,
} from 'canoe';
import { Field, FieldAttributes, Form, Formik } from 'formik';
import { FormikHelpers } from 'formik/dist/types';
import React, { useState } from 'react';

import { useFirstMate } from '@candu-ai/first-mate/api';
import { useEventing } from 'commons/eventing';
import { useAuthentication } from 'commons/hooks';
import { useChildNodes, useMultiNodes, useSelection } from 'craft/hooks';

import { AskAI } from './components/AskAI';

const SuggestionContainer = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.OnyxGrayD20};
    border-radius: ${theme.borderRadius.small};
    display: flex;
    flex-direction: column;
    padding: ${theme.spaces.xs};

    button {
      align-self: flex-end;
    }
  `}
`;

const TextButton = styled.button`
  ${({ theme }) => css`
    background-color: transparent;
    border: none;
    color: ${theme.colors.GlitterGray};
    cursor: pointer;
    font-size: ${theme.fonts.small.fontSize};
    padding: 0;
    position: relative;
    left: -5px;
    top: -3px;

    &:focus,
    &:hover,
    &:active {
      outline: none;
      border: none;
    }
  `}
`;

const FormWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    margin-top: ${theme.spaces.xs};

    button {
      align-self: flex-end;
      margin-top: ${theme.spaces.xs};
    }
  `}
`;

const InputWrapper = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.GlitterGrayL30};
    border-radius: ${theme.spaces.xxs};
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    padding: ${theme.spaces.xxs};

    input {
      background-color: ${theme.colors.GlitterGrayL30};
      border: none;
      width: 100%;

      &:focus,
      &:hover,
      &:active {
        outline: none;
      }
    }

    button {
      font-size: 12px;
      background-color: ${theme.colors.GlitterGrayL30};
      border: 1px solid ${theme.colors.GlitterGrayD10};
      margin-left: ${theme.spaces.xxs};
      margin-top: ${theme.spaces.s};
      padding: ${theme.spaces.xs};
    }
  `}
`;

export const TypographyAI = () => {
  const { selection } = useSelection();
  const childNodes = useChildNodes(selection.first);
  const firstMate = useFirstMate();
  const { track } = useEventing();

  const [loading, setLoading] = useState(false);
  const [prompt, setPromptValue] = useState('');
  const [promptSuggestions, setPromptSuggestions] = useState<string[]>([]);

  const {
    childrenString,
    actions: { setProps },
  } = useMultiNodes([childNodes[0]], (node) => ({
    childrenString: node?.data.props.childrenString,
  }));

  if (!firstMate) {
    return null;
  }

  if (childNodes.length > 1 || !childrenString) {
    // TODO: add support for text with variables in V2
    return null;
  }

  const clearPrompt = () => {
    setPromptValue('');
    setPromptSuggestions([]);
  };

  const applyPrompt = (selectedPromptIndex: number) => {
    setProps({ childrenString: promptSuggestions[selectedPromptIndex] });
    clearPrompt();
  };

  const onSubmit = async (values: any, formikHelpers: FormikHelpers<any>) => {
    if (!values.prompt) {
      return;
    }

    // reset everything on new request
    setPromptValue(values.prompt);
    setPromptSuggestions([]);
    formikHelpers.resetForm();

    track('editor.firstMate.modifyText.promptSubmitted');

    const requestContent = `
        Text: ${values.text}
        Prompt: ${values.prompt}
        `;

    setLoading(true);

    const res = await firstMate.modifyText({
      prompt: requestContent,
    });

    if (res) {
      setPromptSuggestions(res.data?.length ? res.data : [values.text]);
    } else {
      console.log(`An error occurred: ${JSON.stringify(res)}`);
      clearPrompt();
    }

    setLoading(false);
  };

  return (
    <AskAI>
      {({ toggleOpen }) => (
        <Formik
          initialValues={{
            prompt: '',
            text: childrenString,
          }}
          onSubmit={onSubmit}
        >
          {({ setFieldValue, handleSubmit }) => (
            <>
              <Stack space="xs">
                {(loading || promptSuggestions.length > 0) && (
                  <Stack space="xs">
                    <Inline space="xs">
                      <ArrowLeftIcon onClick={clearPrompt} size="normal" color="GlitterGray" />
                      <TextButton onClick={clearPrompt}>{prompt}</TextButton>
                    </Inline>
                    {loading && (
                      <Inline space="xxs" align="center">
                        <Spinner size="small" color="AquaTeal" />
                        <Caption color="OnyxGrayL30" m="none" mt="xxs" ml="xxs">
                          Working on it...
                        </Caption>
                      </Inline>
                    )}
                    {promptSuggestions
                      .filter((_, index) => index < 2)
                      .map((suggestion, index) => (
                        <SuggestionContainer key={suggestion}>
                          <Paragraph font="smaller" size="short" color="White" mb="xs">
                            {suggestion}
                          </Paragraph>

                          <Button
                            variant="white"
                            appearance="outline"
                            size="xsmall"
                            onClick={() => {
                              track('editor.firstMate.modifyText.resultInserted');
                              applyPrompt(index);
                              toggleOpen();
                            }}
                          >
                            Insert
                          </Button>
                        </SuggestionContainer>
                      ))}
                  </Stack>
                )}
              </Stack>
              {!loading && !promptSuggestions.length && (
                <Form>
                  <FormWrapper>
                    <InputWrapper>
                      <Field name="prompt">
                        {({ field }: FieldAttributes<any>) => (
                          <input {...field} rows="2" placeholder="Ask anything..." />
                        )}
                      </Field>
                      <Button
                        variant="white"
                        size="small"
                        onClick={() => {
                          setFieldValue('prompt', 'Summarize text');
                          handleSubmit();
                        }}
                      >
                        Summarize text
                      </Button>
                      <Button
                        variant="white"
                        size="small"
                        onClick={() => {
                          setFieldValue('prompt', 'Fix spelling & grammar');
                          handleSubmit();
                        }}
                      >
                        Fix spelling & grammar
                      </Button>
                    </InputWrapper>
                    <Button variant="secondary" type="submit" disabled={loading} size="small">
                      Preview <EnterIcon ml="xs" mt="xxs" color="White" size="xs" />
                    </Button>
                  </FormWrapper>
                </Form>
              )}
            </>
          )}
        </Formik>
      )}
    </AskAI>
  );
};
